import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { pathParams } from '../../routing/routes';
import { requestDataStorage } from '../../services/api/dataStorage/datastorage.service';
import { checkIdentStatus } from '../../services/api/ident/checkIdentStatus';
import { getCreditApplicationResponse } from '../../services/api/miles/milesIdProvider';
import { debugLog } from '../../services/common/miscUtils';
import { storage } from '../../services/localStorage';
import { store } from '../../services/redux';
import { setCreditApplicationDetails } from '../../services/redux/features/creditApplication.slice';
import { useNavigate } from '../../services/routing/navigate';
import { ApplicantType, IdentStatus } from '../../types/identLandingPage';
import { SpinnerSection } from '../AuthenticatePage/AuthenticatePage';

export const IdentStatusPullPage: FunctionComponent = () => {
  // ms/s * sec/m * min
  const POLLING_TIMEOUT = 1000 * 60 * 3;
  const POLLING_INTERVAL = 1000 * 10;

  const [isLoading, setIsLoading] = useState(false);
  const { identStatusId: id, identStatusRole: role } = useParams<typeof pathParams>();
  const [dataStorageReady, setDataStorageReady] = useState(false);

  const isPendingPollingRequestRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();

  const cleanupTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await requestDataStorage(id as string);
        const { postOnfidoDashboardRedirect } = storage.dashboardOnfidoFlow;

        if (response?.custom?.creditApplicationId && !postOnfidoDashboardRedirect) {
          const creditApplicationResponse = await getCreditApplicationResponse(response?.custom?.creditApplicationId);

          if (creditApplicationResponse) {
            store.dispatch(setCreditApplicationDetails(creditApplicationResponse));
          }
        }

        setDataStorageReady(true);
      } catch (error) {
        debugLog({ message: 'Failed to load transaction data', level: 'ERROR' });
        navigate.error(error);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const navigateBasedOnStatus = useCallback(
    async (response) => {
      const { postOnfidoDashboardRedirect } = storage.dashboardOnfidoFlow;

      if (postOnfidoDashboardRedirect) {
        delete storage.dashboardOnfidoFlow.postOnfidoDashboardRedirect;

        if (response.status === IdentStatus.FAILURE) {
          throw new Error(`Dashboard Onfido flow failed for ${postOnfidoDashboardRedirect}`);
        }

        window.location.href = postOnfidoDashboardRedirect;
      }

      const statusNavigationMap = {
        [IdentStatus.SUCCESS]: () => navigate.identSuccess(`?cid=${id}`),
        [IdentStatus.SUCCESS_APPLICANT]: () => navigate.identSuccessCoApplicant(`?cid=${id}`),
        [IdentStatus.SUCCESS_COAPPLICANT_EMAIL]: () => navigate.identSuccessCoApplicantEmail(`?cid=${id}`),
        [IdentStatus.SUCCESS_COAPPLICANT_LINK]: () => navigate.identSuccessCoApplicantScrive(`?cid=${id}`),
        [IdentStatus.FAILURE]: () => navigate.identFailed(`?cid=${id}`),
      };

      const routeNavigateHandler = statusNavigationMap[response.status];

      if (routeNavigateHandler) {
        cleanupTimers();

        routeNavigateHandler();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cleanupTimers, id, navigate]
  );

  const executeStatusRequest = useCallback(async () => {
    if (isPendingPollingRequestRef.current || !dataStorageReady) {
      return;
    }

    isPendingPollingRequestRef.current = true;

    try {
      const response = await checkIdentStatus(id!, {
        customerRole: role as ApplicantType,
      });
      await navigateBasedOnStatus(response);

      isPendingPollingRequestRef.current = false;
    } catch {
      setIsLoading(false);
      cleanupTimers();

      navigate.error();
    }
  }, [cleanupTimers, dataStorageReady, id, navigate, navigateBasedOnStatus, role]);

  const startPolling = useCallback(async () => {
    intervalRef.current = setInterval(() => executeStatusRequest(), POLLING_INTERVAL);
    timeoutRef.current = setTimeout(() => {
      cleanupTimers();
      navigate.error();
    }, POLLING_TIMEOUT);
  }, [POLLING_INTERVAL, POLLING_TIMEOUT, cleanupTimers, executeStatusRequest, navigate]);

  useEffect(() => {
    setIsLoading(true);
    startPolling();
    executeStatusRequest();
  }, [executeStatusRequest, startPolling]);

  return <>{isLoading ? <SpinnerSection /> : null}</>;
};
